import { markRaw } from "vue";

import PageIcon from "~/components/dumb/PageIcon.vue";
import StatusIcon from "~/components/dumb/StatusIcon.vue";
import { COMPLETED_STATUS_KINDS } from "~/components/visualization/constants";
import { DEFAULT_CHIP_COLOR } from "~/constants/style";
import { IconSize, TypeaheadSpecialEntity, ViewKind } from "~/shared/enums";
import type { TypeaheadGetOptionsFn } from "~/shared/types";
import { useDataStore } from "~/stores";
import { getPageDisplayName } from "~/utils/common";

export const getTaskOptions: TypeaheadGetOptionsFn = async (query) => {
  const dataStore = useDataStore();

  const load = async (offset: number) => {
    const queryNorm = query.toString();
    const results = await dataStore.loadTasks(
      { title: "task".includes(queryNorm) ? "" : query, inTrash: false, isDraft: false },
      offset
    );

    return {
      options: results.data.results.map((task) => {
        const statusKind = dataStore.getStatusByDuid(task.statusDuid)?.kind;
        const dartboard = dataStore.getDartboardByDuid(task.dartboardDuid);
        const pageName = getPageDisplayName(dartboard, dataStore.getSpaceByDuid, "full");
        const spaceAbrev = getPageDisplayName(dartboard, dataStore.getSpaceByDuid, "abrev");
        return {
          value: task.duid,
          label: task.title,
          specialEntity: TypeaheadSpecialEntity.TASK,
          adtlSearchTerms: ["task", pageName, spaceAbrev],
          icon: markRaw(StatusIcon),
          iconArgs: {
            class: "icon-md",
            duid: task.statusDuid,
          },
          chipColorHex: DEFAULT_CHIP_COLOR,
          strikethrough: statusKind && COMPLETED_STATUS_KINDS.has(statusKind),
        };
      }),
      total: results.data.count,
    };
  };

  const initialTasks = await load(0);
  return { key: "tasks", ...initialTasks, loadMore: load };
};

export const getDocOptions: TypeaheadGetOptionsFn = async (query) => {
  const dataStore = useDataStore();

  const load = async (offset: number) => {
    const queryNorm = query.toString();
    const results = await dataStore.loadDocs(
      { search: "doc".includes(queryNorm) ? "" : query, order: "title" },
      offset
    );

    return {
      options: results.data.results.map((doc) => {
        const folder = dataStore.getFolderByDuid(doc.folderDuid);
        const pageName = getPageDisplayName(folder, dataStore.getSpaceByDuid, "full");
        const spaceAbrev = getPageDisplayName(folder, dataStore.getSpaceByDuid, "abrev");

        return {
          value: doc.duid,
          label: doc.title,
          specialEntity: TypeaheadSpecialEntity.DOC,
          adtlSearchTerms: ["doc", pageName, spaceAbrev],
          icon: markRaw(PageIcon),
          iconArgs: {
            page: doc,
          },
          chipColorHex: DEFAULT_CHIP_COLOR,
          chipIcon: markRaw(PageIcon),
          chipIconArgs: {
            page: doc,
            size: IconSize.S,
          },
        };
      }),
      total: results.data.count,
    };
  };

  const initialDocs = await load(0);
  return { key: "docs", ...initialDocs, loadMore: load };
};

export const getSkillOptionsSync = () => {
  const options = useDataStore()
    .skillList()
    .map((skill) => ({
      value: skill.duid,
      label: skill.title,
      specialEntity: TypeaheadSpecialEntity.SKILL,
      adtlSearchTerms: ["skill"],
      icon: PageIcon,
      iconArgs: {
        page: skill,
      },
      chipColorHex: DEFAULT_CHIP_COLOR,
      chipIcon: markRaw(PageIcon),
      chipIconArgs: {
        page: skill,
        size: IconSize.S,
      },
    }));

  return { key: "skills", options, total: options.length };
};

export const getViewOptionsSync = () => {
  const options = useDataStore()
    .viewList.filter((view) => view.kind === ViewKind.CUSTOM)
    .map((view) => ({
      value: view.duid,
      label: view.title,
      specialEntity: TypeaheadSpecialEntity.VIEW,
      adtlSearchTerms: ["view"],
      icon: PageIcon,
      iconArgs: {
        page: view,
      },
      chipColorHex: DEFAULT_CHIP_COLOR,
      chipIcon: markRaw(PageIcon),
      chipIconArgs: {
        page: view,
        size: IconSize.S,
      },
    }));

  return { key: "views", options, total: options.length };
};

export const getDartboardOptionsSync = () => {
  const dataStore = useDataStore();

  const options = dataStore.getDartboardList().map((dartboard) => ({
    value: dartboard.duid,
    label: getPageDisplayName(dartboard, dataStore.getSpaceByDuid, "abrev"),
    specialEntity: TypeaheadSpecialEntity.DARTBOARD,
    adtlSearchTerms: ["dartboard", dartboard.title],
    icon: PageIcon,
    iconArgs: {
      page: dartboard,
    },
    chipColorHex: DEFAULT_CHIP_COLOR,
    chipIcon: markRaw(PageIcon),
    chipIconArgs: {
      page: dartboard,
      size: IconSize.S,
    },
  }));

  return { key: "dartboards", options, total: options.length };
};

export const getFolderOptionsSync = () => {
  const dataStore = useDataStore();

  const options = dataStore.getFolderList().map((folder) => ({
    value: folder.duid,
    label: getPageDisplayName(folder, dataStore.getSpaceByDuid, "abrev"),
    specialEntity: TypeaheadSpecialEntity.FOLDER,
    adtlSearchTerms: ["folder"],
    icon: PageIcon,
    iconArgs: {
      page: folder,
    },
    chipColorHex: DEFAULT_CHIP_COLOR,
    chipIcon: markRaw(PageIcon),
    chipIconArgs: {
      page: folder,
      size: IconSize.S,
    },
  }));

  return { key: "folders", options, total: options.length };
};

export const MINOR_ENTITY_OPTION_FNS_SYNC = [getDartboardOptionsSync, getViewOptionsSync, getFolderOptionsSync];
export const MINOR_ENTITY_OPTION_FNS: TypeaheadGetOptionsFn[] = MINOR_ENTITY_OPTION_FNS_SYNC.map(
  (getOptions) => async () => getOptions()
);
